import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import BrainContentPinnedWave from "../../BrainContentPinnedWave";
import PinnedWavesScrollButton from "./PinnedWavesScrollButton";
import type { PinnedWaveSnapshot } from "@/hooks/usePinnedWaves";
interface PinnedWavesScrollerProps {
  readonly pinnedWaves: readonly PinnedWaveSnapshot[];
  readonly currentWaveId: string | null;
  readonly hoveredWaveId: string | null;
  readonly onHoverWave: (waveId: string) => void;
  readonly onHoverExit: () => void;
  readonly onRemove: (waveId: string) => void;
}
interface ScrollArrowVisibility {
  readonly left: boolean;
  readonly right: boolean;
}
const PinnedWavesScroller: React.FC<PinnedWavesScrollerProps> = t0 => {
  const $ = _c(28);
  const {
    pinnedWaves,
    currentWaveId,
    hoveredWaveId,
    onHoverWave,
    onHoverExit,
    onRemove
  } = t0;
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      left: false,
      right: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [scrollArrowVisibility, setScrollArrowVisibility] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = direction => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      container.scrollBy({
        left: direction === "left" ? -200 : 200,
        behavior: "smooth"
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scrollHorizontally = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (window.matchMedia("(pointer: coarse)").matches) {
        setScrollArrowVisibility(_temp);
        return;
      }
      const container_0 = containerRef.current;
      if (!container_0) {
        return;
      }
      const scrollableWidth = container_0.scrollWidth;
      const viewportWidth = container_0.offsetWidth;
      const currentScroll = container_0.scrollLeft;
      const maxScroll = scrollableWidth - viewportWidth;
      const isAtEnd = Math.abs(currentScroll - maxScroll) < 1;
      const nextVisibility = {
        left: currentScroll > 0,
        right: maxScroll > 0 && !isAtEnd
      };
      setScrollArrowVisibility(current_0 => current_0.left === nextVisibility.left && current_0.right === nextVisibility.right ? current_0 : nextVisibility);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const checkScrollArrows = t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const container_1 = containerRef.current;
      if (!container_1) {
        return;
      }
      const content = contentRef.current;
      let frameId = null;
      const scheduleCheckScrollArrows = () => {
        if (frameId !== null) {
          return;
        }
        frameId = window.requestAnimationFrame(() => {
          frameId = null;
          checkScrollArrows();
        });
      };
      scheduleCheckScrollArrows();
      const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleCheckScrollArrows);
      resizeObserver?.observe(container_1);
      if (content) {
        resizeObserver?.observe(content);
      }
      container_1.addEventListener("scroll", scheduleCheckScrollArrows);
      container_1.addEventListener("load", scheduleCheckScrollArrows, true);
      window.addEventListener("resize", scheduleCheckScrollArrows);
      return () => {
        if (frameId !== null) {
          window.cancelAnimationFrame(frameId);
        }
        resizeObserver?.disconnect();
        container_1.removeEventListener("scroll", scheduleCheckScrollArrows);
        container_1.removeEventListener("load", scheduleCheckScrollArrows, true);
        window.removeEventListener("resize", scheduleCheckScrollArrows);
      };
    };
    t5 = [checkScrollArrows];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const showLeftArrow = scrollArrowVisibility.left;
  const showRightArrow = scrollArrowVisibility.right;
  let t6;
  if ($[5] !== showLeftArrow) {
    t6 = showLeftArrow && <PinnedWavesScrollButton direction="left" onClick={() => scrollHorizontally("left")} />;
    $[5] = showLeftArrow;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== showRightArrow) {
    t7 = showRightArrow && <PinnedWavesScrollButton direction="right" onClick={() => scrollHorizontally("right")} />;
    $[7] = showRightArrow;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== currentWaveId || $[10] !== hoveredWaveId || $[11] !== onHoverExit || $[12] !== onHoverWave || $[13] !== onRemove || $[14] !== pinnedWaves) {
    let t9;
    if ($[16] !== currentWaveId || $[17] !== hoveredWaveId || $[18] !== onHoverExit || $[19] !== onHoverWave || $[20] !== onRemove) {
      t9 = wave => <BrainContentPinnedWave key={wave.id} wave={wave} currentWaveId={currentWaveId} active={currentWaveId === wave.id || hoveredWaveId === wave.id} onMouseEnter={onHoverWave} onMouseLeave={onHoverExit} onRemove={onRemove} />;
      $[16] = currentWaveId;
      $[17] = hoveredWaveId;
      $[18] = onHoverExit;
      $[19] = onHoverWave;
      $[20] = onRemove;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    t8 = pinnedWaves.map(t9);
    $[9] = currentWaveId;
    $[10] = hoveredWaveId;
    $[11] = onHoverExit;
    $[12] = onHoverWave;
    $[13] = onRemove;
    $[14] = pinnedWaves;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <div ref={containerRef} className="no-scrollbar tw-absolute tw-inset-0 tw-flex tw-items-center tw-overflow-x-auto tw-overflow-y-hidden tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div ref={contentRef} className="tw-flex tw-gap-x-3">{t8}</div></div>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t6 || $[25] !== t7 || $[26] !== t9) {
    t10 = <div className="tw-relative tw-mb-2 tw-mr-2 tw-h-8">{t6}{t7}{t9}</div>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};
export default PinnedWavesScroller;
function _temp(current) {
  return current.left || current.right ? {
    left: false,
    right: false
  } : current;
}